import React, { useCallback } from 'react';
import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getPageViewSummary } from '../../api/pageViewLog';
import type { PageViewSummary } from '../../types/PageViewSummary';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// "שימוש במסכים" — read-only analytics viewer. SysAdmin-only (DB resource
// `pageViewLog` grants Edit only to SysAdmin → toolbar Add/Delete hidden).
// Reuses the LopCrudPage shell to inherit search / export / customization /
// context menu. `saveBatch` is a stubbed no-op (LopCrudPage requires the
// prop; it's never invoked since nothing here can become dirty).
//
// daysIdle is computed client-side from lastUsedAt — the SQL stays simple
// (just MAX(lastAt)) and the column adjusts every time the page loads
// without needing a server round trip.

function fmtDateTime(value: unknown): string {
  if (!value) return t('pageViewLog.never');
  return formatDateTimeIL(value, { seconds: true });
}

function daysIdleGetter(p: ValueGetterParams<PageViewSummary>): number | null {
  const last = p.data?.lastUsedAt;
  if (!last) return null;
  const ts = new Date(last).valueOf();
  if (Number.isNaN(ts)) return null;
  return Math.floor((Date.now() - ts) / 86400000);
}

function daysIdleFormatter(p: ValueFormatterParams<PageViewSummary, number | null>): string {
  const v = p.value;
  return v == null ? t('pageViewLog.none') : String(v);
}

const columnDefs: ColDef<PageViewSummary>[] = [
  {
    field: 'pageCode',
    headerName: t('pageViewLog.columns.pageCode'),
    width: 140,
    editable: false,
  },
  {
    field: 'displayName',
    headerName: t('pageViewLog.columns.displayName'),
    flex: 1,
    minWidth: 180,
    editable: false,
  },
  {
    field: 'url',
    headerName: t('pageViewLog.columns.url'),
    width: 200,
    editable: false,
    cellStyle: { direction: 'ltr', textAlign: 'left' },
  },
  {
    field: 'lastUsedAt',
    headerName: t('pageViewLog.columns.lastUsedAt'),
    width: 160,
    editable: false,
    valueFormatter: (p) => fmtDateTime(p.value),
  },
  {
    headerName: t('pageViewLog.columns.daysIdle'),
    colId: 'daysIdle',
    width: 110,
    editable: false,
    cellDataType: 'number',
    valueGetter: daysIdleGetter,
    valueFormatter: daysIdleFormatter,
  },
  {
    field: 'totalViews',
    headerName: t('pageViewLog.columns.totalViews'),
    width: 120,
    editable: false,
    cellDataType: 'number',
  },
  {
    field: 'distinctUsers',
    headerName: t('pageViewLog.columns.distinctUsers'),
    width: 130,
    editable: false,
    cellDataType: 'number',
  },
];

const newRowDefaults = (): Partial<PageViewSummary> => ({});

export function PageViewLogPage() {
  const getRows = useCallback(() => getPageViewSummary(), []);
  // No-op: read-only page (no Edit permission). LopCrudPage requires the
  // saveBatch prop but never invokes it without dirty rows.
  const saveBatch = useCallback(async () => [], []);

  return (
    <LopCrudPage<PageViewSummary>
      resourceCode="pageViewLog"
      titleKey="pageViewLog.pageTitle"
      idField="pageCode"
      getRows={getRows}
      saveBatch={saveBatch}
      columnDefs={columnDefs}
      newRowDefaults={newRowDefaults}
      showAdd={false}
      showDelete={false}
    />
  );
}
